/**
 * Schema-driven import validation that runs in the browser while the user edits cells. The server
 * remains the source of truth (duplicates against existing records, opt-out state…); these checks
 * give instant feedback per keystroke.
 */
import { parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js/min';
import { cellKey, type CellIssue, type RowMark } from './sheet-model';

export interface ImportFieldSpec {
  key: string;
  label: string;
  required?: boolean;
  hint?: string;
  /** Header aliases (case/punctuation-insensitive) for auto-detection. */
  aliases?: string[];
  /** Instant validation. Return an error message, or null when the value is fine. */
  validate?: (value: string) => string | null;
  /** Canonical form used for duplicate detection (return null when the value cannot be normalised). */
  normalize?: (value: string) => string | null;
  /** Duplicate values (after normalize) are flagged. */
  unique?: boolean;
}

export interface ImportSchema {
  fields: ImportFieldSpec[];
  /** Label for a row in messages ("contact", "order"…). */
  noun: string;
}

/** field key → column index (in the sheet). */
export type ColumnMap = Record<string, number>;

export function normalizeHeader(h: string): string {
  return h.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Match header text to schema fields (first match wins per field). */
export function detectMapping(schema: ImportSchema, headers: string[]): ColumnMap {
  const map: ColumnMap = {};
  const used = new Set<number>();
  for (const f of schema.fields) {
    const aliases = new Set([normalizeHeader(f.label), normalizeHeader(f.key), ...(f.aliases ?? []).map(normalizeHeader)]);
    const idx = headers.findIndex((h, i) => !used.has(i) && aliases.has(normalizeHeader(h)));
    if (idx >= 0) {
      map[f.key] = idx;
      used.add(idx);
    }
  }
  return map;
}

/** The most header-like of the first rows: most alias hits, then most filled cells, minus phone-looking cells. */
export function detectHeaderRow(schema: ImportSchema, grid: string[][]): number {
  const aliasSet = new Set(schema.fields.flatMap((f) => [normalizeHeader(f.label), normalizeHeader(f.key), ...(f.aliases ?? []).map(normalizeHeader)]));
  let best = 0;
  let bestScore = -1;
  for (let i = 0; i < Math.min(grid.length, 20); i++) {
    const row = grid[i] ?? [];
    const filled = row.filter((c) => c.trim() !== '').length;
    if (filled < 2) continue;
    const hits = row.filter((c) => aliasSet.has(normalizeHeader(c))).length;
    const numeric = row.filter((c) => /^[\d\s()+.-]{7,}$/.test(c)).length;
    const score = hits * 10 + filled - numeric * 5;
    if (score > bestScore) {
      bestScore = score;
      best = i;
    }
  }
  return best;
}

/** Unique, non-empty header labels (server mapping is by header text). */
export function headerLabels(raw: string[], width: number): string[] {
  const out: string[] = [];
  const seen = new Map<string, number>();
  for (let c = 0; c < width; c++) {
    let h = (raw[c] ?? '').trim() || `Column ${c + 1}`;
    const n = seen.get(h) ?? 0;
    seen.set(h, n + 1);
    if (n > 0) h = `${h} (${n + 1})`;
    out.push(h);
  }
  return out;
}

export interface ClientRowVerdict {
  /** data-row index within the sheet (header is row 0). */
  row: number;
  errors: Array<{ col: number; field: string; message: string }>;
  warnings: Array<{ col: number; field: string; message: string }>;
  empty: boolean;
}

export interface ClientValidation {
  verdicts: ClientRowVerdict[];
  cells: Map<string, CellIssue>;
  errorRows: number;
  warningRows: number;
  emptyRows: number;
  missingRequired: string[];
}

/** Validate every data row (rows 1..n when row 0 is the header). */
export function validateSheet(schema: ImportSchema, rows: string[][], mapping: ColumnMap, opts: { headerRows: number }): ClientValidation {
  const cells = new Map<string, CellIssue>();
  const verdicts: ClientRowVerdict[] = [];
  const seen = new Map<string, Map<string, number>>(); // field → normalized value → first row
  const missingRequired = schema.fields.filter((f) => f.required && mapping[f.key] === undefined).map((f) => f.label);
  let errorRows = 0,
    warningRows = 0,
    emptyRows = 0;
  for (let r = opts.headerRows; r < rows.length; r++) {
    const row = rows[r] ?? [];
    const v: ClientRowVerdict = { row: r, errors: [], warnings: [], empty: row.every((c) => (c ?? '').trim() === '') };
    if (v.empty) {
      emptyRows++;
      verdicts.push(v);
      continue;
    }
    for (const f of schema.fields) {
      const col = mapping[f.key];
      if (col === undefined) continue;
      const raw = (row[col] ?? '').trim();
      if (!raw) {
        if (f.required) {
          v.errors.push({ col, field: f.key, message: `${f.label} is required` });
          cells.set(cellKey(r, col), { level: 'error', message: `${f.label} is required` });
        }
        continue;
      }
      const err = f.validate?.(raw) ?? null;
      if (err) {
        v.errors.push({ col, field: f.key, message: err });
        cells.set(cellKey(r, col), { level: 'error', message: err });
        continue;
      }
      if (f.unique) {
        const key = (f.normalize ? f.normalize(raw) : raw.toLowerCase()) ?? raw.toLowerCase();
        let m = seen.get(f.key);
        if (!m) seen.set(f.key, (m = new Map()));
        const first = m.get(key);
        if (first !== undefined) {
          const msg = `Duplicate ${f.label.toLowerCase()} (same as row ${first + 1})`;
          v.warnings.push({ col, field: f.key, message: msg });
          cells.set(cellKey(r, col), { level: 'warning', message: msg });
        } else m.set(key, r);
      }
    }
    if (v.errors.length) errorRows++;
    else if (v.warnings.length) warningRows++;
    verdicts.push(v);
  }
  return { verdicts, cells, errorRows, warningRows, emptyRows, missingRequired };
}

// ── common validators ────────────────────────────────────────────────────────

export function phoneValidator(defaultCountry: string): (v: string) => string | null {
  return (v) => (parsePhone(v, defaultCountry) ? null : 'Not a valid mobile number');
}

export function phoneNormalizer(defaultCountry: string): (v: string) => string | null {
  return (v) => parsePhone(v, defaultCountry);
}

export function parsePhone(v: string, defaultCountry: string): string | null {
  try {
    const p = parsePhoneNumberFromString(v.replace(/[​-‍﻿]/g, ''), defaultCountry.toUpperCase() as CountryCode);
    return p && p.isValid() ? p.number : null;
  } catch {
    return null;
  }
}

export const emailValidator = (v: string): string | null => (/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v) ? null : 'Not a valid email address');

export function oneOfValidator(options: string[], label: string): (v: string) => string | null {
  const set = new Set(options.map((o) => o.toLowerCase()));
  return (v) => (set.has(v.toLowerCase()) ? null : `Unknown ${label}`);
}

/** Row gutter marks from client verdicts + server results. */
export interface ServerRowVerdict {
  index: number; // zero-based index into the posted data rows
  status: 'create' | 'update' | 'skip' | 'error';
  reason?: string;
  field?: string;
}

export function buildRowMarks(client: ClientValidation, server: ServerRowVerdict[] | null, headerRows: number, nounLabel: (status: ServerRowVerdict['status']) => string): Map<number, RowMark> {
  const marks = new Map<number, RowMark>();
  for (const v of client.verdicts) {
    if (v.empty) continue;
    if (v.errors.length) marks.set(v.row, { level: 'error', label: 'Fix', message: v.errors[0]?.message });
    else if (v.warnings.length) marks.set(v.row, { level: 'warning', label: 'Check', message: v.warnings[0]?.message });
  }
  if (server) {
    for (const s of server) {
      const row = s.index + headerRows;
      if (marks.get(row)?.level === 'error') continue;
      const level = s.status === 'error' ? 'error' : s.status === 'skip' ? 'warning' : s.status === 'update' ? 'info' : 'success';
      marks.set(row, { level, label: nounLabel(s.status), message: s.reason });
    }
  }
  return marks;
}
